import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { getApiErrorMessage } from '@/lib/api/error';
import {
  UserNotificationPreferencesService,
  userNotificationPreferencesQueryKey,
} from '@/services/user-notification-preferences.service';
import { useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const descriptions = {
  subscribed: 'Ativas neste dispositivo.',
  idle: 'Receba avisos de chamadas da fila e dos seus agendamentos, mesmo com o app fechado.',
  denied: 'Avisos bloqueados. Permita as notificações nas configurações deste navegador e recarregue a página.',
  unsupported: 'Este navegador não permite receber avisos com o app fechado.',
  error: 'Não foi possível consultar a configuração atual.',
  checking: 'Verificando este dispositivo…',
  loading: 'Verificando este dispositivo…',
};

export function PushNotificationSettings({ queue = false }: { queue?: boolean }) {
  const queryClient = useQueryClient();
  const { state, enablePush, disablePush } = usePushNotifications();

  const enable = async () => {
    try {
      if (await enablePush()) {
        const preferences = await UserNotificationPreferencesService.update({ pushEnabled: true });
        queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);
        toast.success('Notificações em tempo real ativadas');
      }
    } catch (error) {
      toast.error('Não foi possível ativar', {
        description: getApiErrorMessage(error, 'Confira as configurações do navegador.'),
      });
    }
  };

  const disable = async () => {
    try {
      const preferences = await UserNotificationPreferencesService.update({ pushEnabled: false });
      queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);
      await disablePush();
      toast.success('Notificações em tempo real desativadas');
    } catch (error) {
      toast.error('Não foi possível desativar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  const isLoading = state === 'checking' || state === 'loading';
  const isSubscribed = state === 'subscribed';

  return (
    <section
      className={`flex flex-col gap-4 rounded-2xl border p-5 ${queue ? 'border-primary/30 bg-primary-light' : 'bg-card mb-6 sm:flex-row sm:items-center sm:justify-between'}`}
    >
      <div className="flex min-w-0 gap-3">
        <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          {state === 'denied' ? <BellOff className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
        </span>
        <div>
          <h2 className="text-sm font-semibold">{queue ? 'Não perca sua vez' : 'Notificações em tempo real'}</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {queue && state === 'idle'
              ? 'Receba um aviso quando você for o próximo e quando chegar sua vez, mesmo com o app fechado.'
              : descriptions[state]}
          </p>
        </div>
      </div>

      {isSubscribed ? (
        <Button variant="outline" className="min-h-11 shrink-0" onClick={() => void disable()}>
          Desativar notificações
        </Button>
      ) : state === 'idle' || state === 'error' ? (
        <Button className="min-h-11 shrink-0" onClick={() => void enable()}>
          Ativar notificações
        </Button>
      ) : isLoading ? (
        <Loader2
          className="text-muted-foreground h-5 w-5 animate-spin self-center"
          aria-label="Verificando notificações"
        />
      ) : null}
    </section>
  );
}
